import { useRef, useState } from "react";
import MathsPreview from "./MathsPreview";
import { Calculator as CalcIcon, Keyboard, X } from "lucide-react";

/**
 * The box a child types their answer into.
 *
 * It stays a plain text input. That is a decision, not a shortcut: the mark
 * scheme's answer is stored as text, the checker compares text, and a child
 * reading an answer back over the phone reads text. A rich editor would have
 * to be converted back to text to be worth anything, and the conversion is
 * where answers get silently changed.
 *
 * What it adds on top of the box is the two things a plain box cannot do on a
 * phone: a keypad for the symbols that are not on a phone keyboard, and a
 * preview that draws "3/8" as a stacked fraction so a child can see that what
 * they typed means what they meant.
 *
 * The keypad inserts at the cursor rather than appending, because a child who
 * has typed "x" and then wants a power expects it next to the x.
 */

const KEYS = [
  [
    { t: "7" }, { t: "8" }, { t: "9" },
    { t: "/", label: "a⁄b", hint: "fraction" },
    { t: "(", label: "(" }, { t: ")", label: ")" },
  ],
  [
    { t: "4" }, { t: "5" }, { t: "6" },
    { t: "^", label: "xⁿ", hint: "power" },
    { t: "^2", label: "x²" }, { t: "^3", label: "x³" },
  ],
  [
    { t: "1" }, { t: "2" }, { t: "3" },
    { t: "sqrt(", label: "√", hint: "square root" },
    { t: "π", label: "π" }, { t: "°", label: "°" },
  ],
  [
    { t: "0" }, { t: "." }, { t: "-", label: "−" },
    { t: "x" }, { t: "y" }, { t: ":", label: ":", hint: "ratio" },
  ],
  [
    { t: "," }, { t: "+" }, { t: "<" }, { t: ">" },
    { t: "≤", label: "≤" }, { t: "≥", label: "≥" },
  ],
];

export default function MathsAnswer({
  value,
  onChange,
  onCommit,
  disabled,
  placeholder = "type it here",
  id,
  onOpenCalculator,
}) {
  const [padOpen, setPadOpen] = useState(false);
  const ref = useRef(null);

  const insert = (text) => {
    const el = ref.current;
    const current = value ?? "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + text + current.slice(end);
    onChange(next);
    // Put the caret after what was inserted, once React has re-rendered.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const at = start + text.length;
      el.setSelectionRange(at, at);
    });
  };

  const backspace = () => {
    const el = ref.current;
    const current = value ?? "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    if (start === 0 && end === 0) return;
    const from = start === end ? start - 1 : start;
    onChange(current.slice(0, from) + current.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(from, from);
    });
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          ref={ref}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onCommit && onCommit()}
          placeholder={disabled ? "—" : placeholder}
          inputMode="text"
          autoComplete="off"
          className="font-mono text-sm border border-slate-300 rounded-lg px-3 py-1.5 w-[200px] disabled:bg-slate-50"
        />
        {!disabled && (
          <>
            <button type="button" onClick={() => setPadOpen((v) => !v)}
              title="Symbols for fractions, powers and roots"
              className={`inline-flex items-center gap-1 text-xs rounded-lg border px-2 py-1.5 ${
                padOpen ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
              {padOpen ? <X size={12} /> : <Keyboard size={12} />} Symbols
            </button>
            {onOpenCalculator && (
              <button type="button" onClick={onOpenCalculator}
                title="Open the calculator"
                className="inline-flex items-center gap-1 text-xs rounded-lg border border-slate-300 px-2 py-1.5 hover:bg-slate-50">
                <CalcIcon size={12} /> Calculator
              </button>
            )}
          </>
        )}
        {/* The preview only earns its place when it shows something the box
            cannot - a fraction, a power, a root. For "7" it would be noise. */}
        {!!(value || "").trim() && /[/^]|sqrt|√/i.test(value) && (
          <span className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
            <MathsPreview value={value} />
          </span>
        )}
      </div>

      {padOpen && !disabled && (
        <div className="mt-2 inline-block rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          {KEYS.map((row, r) => (
            <div key={r} className="flex gap-1 mb-1 last:mb-0">
              {row.map((k) => (
                <button key={k.t} type="button" title={k.hint} onClick={() => insert(k.t)}
                  className="w-10 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm font-medium">
                  {k.label ?? k.t}
                </button>
              ))}
            </div>
          ))}
          <div className="flex gap-1">
            <button type="button" onClick={backspace}
              className="w-[84px] h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm">
              &larr; delete
            </button>
            <button type="button" onClick={() => onChange("")}
              className="w-[84px] h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm">
              clear
            </button>
            <button type="button" onClick={() => { setPadOpen(false); onCommit && onCommit(); }}
              className="flex-1 h-9 rounded-lg bg-slate-800 text-white text-sm font-semibold px-3">
              Done
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 max-w-[260px]">
            Write a fraction as 3/8 and a power as x^2 &mdash; the preview shows how it reads.
          </p>
        </div>
      )}
    </div>
  );
}
